(function () {
  'use strict';
  const BASE = 'https://www.liqui-moly.com/en/us/rest/V1/';
  const GUIDE = 'https://www.liqui-moly.com/en/us/service/oil-guide.html';
  const FIELDS = ['year', 'make', 'model', 'engine'];
  const SOURCE_HOLDS = new Set(['60268', '60294', '60292', '60293', '55286', '55250']);
  const sourceHold = (type, year) => SOURCE_HOLDS.has(String(type)) || (String(type) === '95502' && Number(year) === 2007);
  const numberId = value => Number.isSafeInteger(Number(value)) && Number(value) > 0 ? String(value) : '';
  const plain = value => typeof value === 'string' ? value.replace(/<[^>]*>/g, ' ').replace(/&(?:nbsp|amp|lt|gt|quot|#39);/g, entity => ({ '&nbsp;': ' ', '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"', '&#39;': "'" }[entity])).replace(/\s+/g, ' ').trim() : '';
  const garageName = (value, make = false) => typeof value === 'string' ? value.normalize('NFKC').trim().replace(/\s+/g, ' ').replace(make ? /\s*\((?:USA|US|UNITED STATES|USA\s*\/\s*CAN)\)$/i : /$^/, '').trim().toLowerCase() : '';
  const garageMatches = (garage, vehicle) => !!garage && !!vehicle && Number(garage.year) === Number(vehicle.year) && !!garageName(garage.make, true) && garageName(garage.make, true) === garageName(vehicle.make, true) && !!garageName(garage.model) && garageName(garage.model) === garageName(vehicle.model);
  const rows = value => Array.isArray(value) ? value : value && typeof value === 'object' ? Object.values(value) : [];
  function yearIn(row, year, prefix = '') {
    const start = Number(row?.[prefix + 'year_start']);
    const endValue = row?.[prefix + 'year_end'];
    const end = endValue === null || endValue === '' ? new Date().getFullYear() + 1 : Number(endValue);
    return Number.isInteger(Number(year)) && Number.isInteger(start) && start >= 1900 && Number.isInteger(end) && end >= start && Number(year) >= start && Number(year) <= end;
  }
  function usMarket(label) {
    const text = String(label || '').toUpperCase();
    if (/\b(?:EXCEPT|EXCLUDING|NOT|NON)[\s-]*(?:USA|US|UNITED STATES)\b/.test(text)) return false;
    const groups = [...text.matchAll(/\(([^)]*)\)/g)].map(match => match[1]);
    return groups.every(group => {
      if (/\b(?:USA|US|UNITED STATES)\b/.test(group)) return true;
      if (/^\d+(?:\.\d+)?\s*(?:KW|HP|PS)$/.test(group.trim())) return true;
      const words = group.match(/\b[A-Z]+\b/g) || [];
      return words.every(word => ['RWD', 'AWD', 'FWD', 'LHD', 'RHD', 'LWB', 'SWB', 'AT', 'MT', 'CVT', 'DCT', 'HYBRID', 'DIESEL', 'PETROL', 'GASOLINE', 'TURBO', 'ELECTRIC'].includes(word));
    });
  }
  function uniqueRows(value, field) {
    if (!Array.isArray(value) || value.length > 5000) throw new Error('Invalid vehicle list');
    const unique = new Map();
    for (const row of value) {
      const id = numberId(row?.id);
      if (!id || !plain(row?.[field])) continue;
      if (unique.has(id)) throw new Error('Ambiguous vehicle identity');
      unique.set(id, row);
    }
    return [...unique.values()];
  }
  function makeChoices(value, year) {
    return uniqueRows(value, 'make_name').filter(row => usMarket(row.make_name) && (!year || yearIn(row, year, 'type_'))).map(row => ({ value: numberId(row.id), label: plain(row.make_name), row })).sort((a, b) => a.label.localeCompare(b.label));
  }
  function modelChoices(value, year) {
    return uniqueRows(value, 'model_name').filter(row => yearIn(row, year, row.type_year_start ? 'type_' : '')).map(row => ({ value: numberId(row.id), label: plain(row.name || row.model_name), row })).sort((a, b) => a.label.localeCompare(b.label));
  }
  function typeChoices(value, year) {
    return uniqueRows(value, 'type_name').filter(row => usMarket(row.type_name) && yearIn(row, year)).map(row => {
      const details = [plain(row.name || row.type_name), plain(row.code), plain(row.fuel), Number(row.cylinder_c_c) > 0 ? `${Number(row.cylinder_c_c)} cc` : '', Number(row.power_h_p) > 0 ? `${Number(row.power_h_p)} hp` : '', plain(row.drive_type)].filter(Boolean);
      return { value: numberId(row.id), label: details.join(' · '), row };
    }).sort((a, b) => a.label.localeCompare(b.label));
  }
  function normalizeRecommendation(payload, selected, variants) {
    if (sourceHold(selected.type.value, selected.year)) { const error = new Error('Vehicle source requires confirmation'); error.code = 'SOURCE_REVIEW'; throw error; }
    if (!Array.isArray(payload) || payload.length !== 1) throw new Error('Invalid recommendation response');
    const response = payload[0];
    if (!response || numberId(response.id) !== selected.type.value || numberId(response.make_id) !== selected.make.value || numberId(response.model_id) !== selected.model.value || Number(response.category_id) !== 1 || !yearIn(response, selected.year) || !yearIn(selected.type.row, selected.year) || plain(response.type_name) !== plain(selected.type.row.type_name) || plain(response.model_name) !== plain(selected.model.row.model_name) || plain(response.make_name) !== plain(selected.make.row.make_name) || !usMarket(response.type_name) || !usMarket(response.make_name)) throw new Error('Vehicle identity mismatch');
    for (const field of ['code', 'fuel', 'fuel_id', 'drive_type', 'cylinder_c_c', 'engine_build', 'power_h_p', 'power_k_w', 'cylinder_count', 'valve_count']) {
      const expected = selected.type.row[field];
      if (expected !== undefined && expected !== null && String(expected).trim() !== '' && String(response[field] ?? '').trim() !== String(expected).trim()) throw new Error('Vehicle specification mismatch');
    }
    const components = rows(response.components).filter(row => row && typeof row === 'object');
    const engines = components.filter(row => Number(row.componentCategoryId) === 1 && /^engine$/i.test(plain(row.name)));
    if (engines.length !== 1) throw new Error('Ambiguous engine');
    const engine = engines[0];
    const skuMap = new Map();
    for (const variant of variants) {
      const sku = String(variant.sku || '').trim();
      if (!sku || !numberId(variant.id) || !numberId(variant.product?.id)) continue;
      const current = skuMap.get(sku) || []; current.push(variant); skuMap.set(sku, current);
    }
    const recommendations = [], found = new Set();
    const sourceUrl = new URL(GUIDE);
    const append = (product, application, additive) => {
      if (!product || typeof product !== 'object' || !Array.isArray(product.articles)) return;
      for (const article of product.articles) {
        const sku = typeof article?.sku === 'string' ? article.sku.trim() : '';
        const matches = skuMap.get(sku) || [];
        if (matches.length !== 1) continue;
        const variant = matches[0], category = variant.product.category;
        if (additive ? !['fuel', 'engine'].includes(category) : category !== 'oils') continue;
        const key = numberId(variant.id) + '|' + application;
        if (found.has(key)) continue;
        found.add(key);
        recommendations.push({ verified: true, sku, product_id: numberId(variant.product.id), variant_id: numberId(variant.id), application, notes: plain(product.productNote || product.includeNote), source_url: sourceUrl.href, kind: additive ? 'additive' : 'oil', source_family: plain(product.sku || product.productNr) });
      }
    };
    rows(engine.products).forEach(product => append(product, plain(engine.name), false));
    components.forEach(component => rows(component.additives).forEach(product => append(product, plain(component.name), true)));
    const capacities = [];
    if (engine.capacities && typeof engine.capacities === 'object') {
      Object.entries(engine.capacities).forEach(([group, values]) => rows(values).forEach(value => {
        if (!value || typeof value !== 'object') return;
        const amount = typeof value.value === 'number' ? String(value.value) : plain(value.value);
        if (!amount) return;
        capacities.push({ label: plain(value.item) || plain(group), value: amount, unit: plain(value.unit), condition: plain(value.condition) });
      }));
    }
    return { id: 'lm-us-' + selected.type.value, verified: true, year: Number(selected.year), make: plain(response.make_name), model: plain(response.model_name), engine: [plain(engine.code), plain(response.type_name), plain(response.fuel), plain(response.drive_type)].filter(Boolean).join(' · '), fuel: plain(response.fuel), engine_details: { type_name: plain(response.type_name), code: plain(engine.code), displacement_cc: Number(response.cylinder_c_c) || null, power_kw: Number(response.power_k_w) || null, drive_type: plain(response.drive_type) }, market: 'USA', source_type_id: selected.type.value, source_make_id: selected.make.value, source_model_id: selected.model.value, source_url: sourceUrl.href, notes: plain(response.vehicle_type), recommendations, capacities, disclaimer: plain(response.disclaimer), additivesVerified: recommendations.some(row => row.kind === 'additive'), matchedProductCount: new Set(recommendations.map(row => row.product_id)).size };
  }
  class LiveGuide {
    constructor(host) {
      this.host = host; this.sequence = 0; this.active = null; this.destroyed = false; this.ready = false; this.failedStage = ''; this.makes = []; this.models = []; this.types = [];
      this.fetcher = typeof fetch === 'function' ? fetch.bind(globalThis) : null;
      this.garage = typeof window !== 'undefined' ? window.SacGarageSelection : null;
      this.garageEpoch = 0;
      this.garageUnsubscribe = this.garage?.subscribe?.(vehicle => {
        this.garageChanged = true; this.garageEpoch += 1;
        if (this.garageManualChange) return;
        if (this.ready && this.host.loaded) void this.restoreGarageSelection(vehicle);
        else this.resetGarage(false);
      });
      host.controller?.signal.addEventListener('abort', () => this.destroy(), { once: true });
    }
    invalidate() { this.sequence += 1; this.active?.abort(); this.active = null; return this.sequence; }
    current(token) { return !this.destroyed && token === this.sequence && !this.host.controller?.signal.aborted; }
    async request(path, token) {
      if (!/^(?:olyslager\/(?:makes|models|types)|recommendations)\/\d+$/.test(path) || !this.fetcher) throw new Error('Invalid guide request');
      const controller = new AbortController(); this.active = controller;
      const timer = setTimeout(() => controller.abort(), 12000);
      try {
        const response = await this.fetcher(BASE + path, { method: 'GET', credentials: 'omit', cache: 'no-store', mode: 'cors', redirect: 'error', referrerPolicy: 'no-referrer', headers: { Accept: 'application/json' }, signal: controller.signal });
        if (!response.ok || !String(response.headers.get('content-type') || '').includes('application/json')) throw new Error('Guide unavailable');
        const data = await response.json();
        if (!this.current(token)) throw new Error('Superseded guide request');
        return data;
      } finally { clearTimeout(timer); if (this.active === controller) this.active = null; }
    }
    status(key, values) { this.host.finderStatus(this.host.t(key, values)); }
    setButton(disabled, retry = false) {
      const button = this.host.querySelector('[data-lm-find]');
      if (!button) return;
      button.disabled = disabled; button.formNoValidate = retry;
      if (this.host.config?.compact) button.hidden = !!this.host.vehicle && !retry;
      if (button.firstChild) button.firstChild.textContent = this.host.t(retry ? 'guide_live_retry' : 'find_products') + ' ';
    }
    clearResult() {
      this.host.setGuideBusy?.(false);
      this.host.filterAddon?.resetForVehicle();
      this.host.vehicle = null;
      const result = this.host.querySelector('[data-lm-vehicle-result]'); if (result) result.hidden = true;
      const meta = this.host.querySelector('[data-lm-guide-meta]'); if (meta) { meta.replaceChildren(); meta.hidden = true; }
      this.host.render();
    }
    async initialize() {
      const token = this.invalidate(); this.failedStage = ''; this.ready = false;
      FIELDS.forEach(field => this.host.setFieldOptions(field, [])); this.setButton(true); this.status('guide_live_loading');
      try {
        const data = await this.request('olyslager/makes/1', token);
        this.makes = makeChoices(data);
        if (!this.makes.length) throw new Error('Empty make list');
        const starts = this.makes.map(item => Number(item.row.type_year_start)).filter(year => Number.isInteger(year) && year >= 1900);
        if (!starts.length) throw new Error('Missing years');
        const latest = new Date().getFullYear() + 1, earliest = Math.min(...starts);
        this.host.setFieldOptions('year', Array.from({ length: latest - earliest + 1 }, (_, index) => String(latest - index)));
        this.ready = true; this.status('guide_live_ready');
      } catch {
        if (!this.current(token)) return;
        this.failedStage = 'initialize'; this.status('guide_live_error'); this.setButton(false, true);
      }
    }
    async change(name) {
      if (!FIELDS.includes(name)) return;
      const token = this.invalidate(); this.failedStage = ''; this.clearResult();
      const index = FIELDS.indexOf(name);
      FIELDS.slice(index + 1).forEach(field => this.host.setFieldOptions(field, []));
      if (index < 2) this.models = [];
      if (index < 3) this.types = [];
      this.setButton(true);
      const values = this.host.fieldValues();
      if (!values[name]) { this.status('guide_live_ready'); return; }
      try {
        if (name === 'year') {
          const eligible = makeChoices(this.makes.map(item => item.row), values.year);
          this.host.setFieldOptions('make', eligible); this.status(eligible.length ? 'guide_live_ready' : 'guide_live_none');
        } else if (name === 'make') {
          const selected = this.makes.find(item => item.value === values.make && yearIn(item.row, values.year, 'type_'));
          if (!selected) throw new Error('Invalid make');
          this.status('guide_live_loading');
          this.models = modelChoices(await this.request('olyslager/models/' + selected.value, token), values.year);
          this.host.setFieldOptions('model', this.models); this.status(this.models.length ? 'guide_live_ready' : 'guide_live_none');
        } else if (name === 'model') {
          const selected = this.models.find(item => item.value === values.model);
          if (!selected) throw new Error('Invalid model');
          this.status('guide_live_loading');
          this.types = typeChoices(await this.request('olyslager/types/' + selected.value, token), values.year);
          this.host.setFieldOptions('engine', this.types); this.status(this.types.length ? 'guide_choose_exact' : 'guide_live_none');
        } else {
          const selected = this.types.find(item => item.value === values.engine);
          if (!selected) throw new Error('Invalid type');
          this.setButton(false); this.status('guide_choose_exact');
          if (this.host.loaded) await this.find();
        }
      } catch {
        if (!this.current(token)) return;
        this.failedStage = name; this.status('guide_live_error'); this.setButton(false, true);
      }
    }
    async restoreReference(reference) {
      if (!this.ready || !this.host.loaded || !reference || !FIELDS.every(field => /^[1-9]\d*$/.test(String(reference[field])) && numberId(reference[field]))) return false;
      for (const field of FIELDS) {
        if (this.destroyed || !this.host.setFieldValue(field, reference[field])) { this.status('guide_restore_invalid'); return false; }
        const pending = this.change(field), token = this.sequence;
        await pending;
        if (!this.current(token) || this.failedStage) return false;
      }
      return this.host.vehicle?.source_type_id === String(reference.engine);
    }
    garageFieldChanged(name) {
      this.garageEpoch += 1;
      if (!['year', 'make', 'model'].includes(name)) return;
      this.garageConstraint = null;
      this.garageManualChange = true;
      try { if (this.garage?.getVehicle?.()) this.garage.select?.(null); }
      finally { this.garageManualChange = false; }
    }
    resetGarage(cancel = true) {
      if (cancel) this.invalidate();
      this.garageConstraint = null;
      this.clearResult();
      const applying = this.host.applyingGuideVehicle;
      this.host.applyingGuideVehicle = true;
      try { this.host.service?.clearVehicle?.(); }
      finally { this.host.applyingGuideVehicle = applying; }
      this.host.setFieldValue('year', '');
      FIELDS.slice(1).forEach(field => this.host.setFieldOptions(field, []));
      this.models = []; this.types = [];
      this.setButton(true);
    }
    async restoreInitialGarage(reference) {
      const vehicle = this.garage?.getVehicle?.();
      if (vehicle) { await this.restoreGarageSelection(vehicle, reference); return true; }
      return !!this.garageChanged;
    }
    async restoreGarageSelection(vehicle, reference = null) {
      const epoch = ++this.garageEpoch;
      this.resetGarage(this.ready);
      if (!vehicle || !this.ready || !this.host.loaded || this.destroyed) {
        if (this.ready) this.status('guide_live_ready');
        return false;
      }
      this.garageConstraint = { year: vehicle.year, make: vehicle.make, model: vehicle.model };
      const stored = this.garage?.getGuideReference?.(vehicle.id), candidate = stored || reference;
      const current = () => !this.destroyed && epoch === this.garageEpoch && !this.host.controller?.signal.aborted;
      const restoreToken = {};
      this.host.restoreToken = restoreToken; this.host.restoringReference = true;
      try {
        if (candidate && Number(candidate.year) === Number(vehicle.year)) {
          const restored = await this.restoreReference(candidate);
          if (!current()) return false;
          if (restored && garageMatches(vehicle, this.host.vehicle)) return true;
          if (stored) this.garage?.clearGuideReference?.(vehicle.id);
          if (this.failedStage) return false;
          this.resetGarage();
          this.garageConstraint = { year: vehicle.year, make: vehicle.make, model: vehicle.model };
        } else if (stored) this.garage?.clearGuideReference?.(vehicle.id);
        return await this.restoreSavedVehicle(vehicle, epoch);
      } finally {
        if (this.host.restoreToken === restoreToken) this.host.restoringReference = false;
      }
    }
    async restoreSavedVehicle(vehicle, epoch = this.garageEpoch) {
      if (!this.ready || !this.host.loaded || !vehicle || !Number.isInteger(Number(vehicle.year)) || !garageName(vehicle.make, true) || !garageName(vehicle.model)) return false;
      const advance = async (field, value) => {
        if (this.destroyed || epoch !== this.garageEpoch || !this.host.setFieldValue(field, value)) return false;
        const pending = this.change(field), token = this.sequence;
        await pending;
        return epoch === this.garageEpoch && this.current(token) && !this.failedStage;
      };
      if (!await advance('year', String(vehicle.year))) { if (epoch === this.garageEpoch && !this.failedStage) this.status('garage_guide_unmatched'); return false; }
      const makes = makeChoices(this.makes.map(item => item.row), vehicle.year).filter(item => garageName(item.row.make_name, true) === garageName(vehicle.make, true));
      if (makes.length !== 1) { this.status('garage_guide_unmatched'); return false; }
      if (!await advance('make', makes[0].value)) return false;
      const models = this.models.filter(item => garageName(item.row.model_name) === garageName(vehicle.model));
      if (models.length !== 1) { this.status('garage_guide_unmatched'); return false; }
      if (!await advance('model', models[0].value)) return false;
      if (this.types.length !== 1) { this.status(this.types.length ? 'garage_guide_choose_exact' : 'garage_guide_unmatched'); return false; }
      if (!await advance('engine', this.types[0].value)) return false;
      return garageMatches(vehicle, this.host.vehicle);
    }
    async find() {
      if (this.failedStage === 'initialize' || !this.ready) { await this.initialize(); if (this.ready) await this.restoreInitialGarage(null); return; }
      if (this.failedStage) { await this.change(this.failedStage); return; }
      const values = this.host.fieldValues();
      const selected = { year: values.year, make: this.makes.find(row => row.value === values.make), model: this.models.find(row => row.value === values.model), type: this.types.find(row => row.value === values.engine) };
      if (!selected.make || !selected.model || !selected.type || !yearIn(selected.type.row, selected.year)) { this.status('guide_choose_exact'); return; }
      if (!this.host.loaded) { this.status('guide_live_loading'); return; }
      const token = this.invalidate(); this.clearResult(); this.setButton(true); this.host.setGuideBusy?.(true); this.status('guide_live_searching');
      try {
        const data = await this.request('recommendations/' + selected.type.value, token);
        const vehicle = normalizeRecommendation(data, selected, [...this.host.variants.values()]);
        if (!this.current(token)) return;
        if (this.garageConstraint && !garageMatches(this.garageConstraint, vehicle)) throw new Error('Garage vehicle mismatch');
        this.host.vehicle = vehicle; this.host.category = 'all';
        const garage = this.garage?.getVehicle?.();
        if (garageMatches(garage, vehicle)) this.garage?.setGuideReference?.(garage.id, { year: String(vehicle.year), make: vehicle.source_make_id, model: vehicle.source_model_id, engine: vehicle.source_type_id });
        const result = this.host.querySelector('[data-lm-vehicle-result]');
        const name = this.host.querySelector('[data-lm-vehicle-name]'); if (name) name.textContent = `${vehicle.year} ${vehicle.make} ${vehicle.model} · ${vehicle.engine}`;
        const notes = this.host.querySelector('[data-lm-vehicle-notes]'); if (notes) notes.textContent = vehicle.notes;
        const source = this.host.querySelector('[data-lm-vehicle-source]'); if (source) source.href = vehicle.source_url;
        if (result) result.hidden = false;
        this.renderMeta(vehicle);
        if (typeof this.host.applyGuideVehicle === 'function') this.host.applyGuideVehicle(); else this.host.render();
        if (this.host.guideResultStatus?.(vehicle) === true) {
        } else if (this.host.config?.compact && typeof this.host.quickProducts === 'function') {
          const count = this.host.quickProducts().length;
          this.status(count ? count === 1 ? 'home_found_one' : 'home_found' : 'home_none', { count });
        } else this.status(vehicle.recommendations.length ? vehicle.matchedProductCount === 1 ? 'guide_live_found_one' : 'guide_live_found' : 'guide_live_none', { count: vehicle.matchedProductCount });
      } catch (error) {
        if (!this.current(token)) return;
        this.clearResult(); this.host.filterAddon?.discardRestore(); this.status(error.code === 'SOURCE_REVIEW' ? 'guide_source_hold' : 'guide_live_error');
      } finally { if (this.current(token)) { this.setButton(false); this.host.setGuideBusy?.(false); } }
    }
    renderMeta(vehicle) {
      const root = this.host.querySelector('[data-lm-guide-meta]'); if (!root) return;
      root.replaceChildren(); root.hidden = false;
      const append = (tag, text, parent = root) => { const node = document.createElement(tag); node.textContent = text; parent.append(node); return node; };
      append('p', this.host.t('guide_attribution'));
      if (['fuel', 'engine'].includes(this.host.config?.product?.category)) return;
      if (!vehicle.additivesVerified) append('p', this.host.t('guide_additives_unverified'));
      if (vehicle.capacities.length || vehicle.disclaimer) {
        const detail = document.createElement('details'); root.append(detail);
        append('summary', this.host.t('guide_source_disclaimer'), detail);
        if (vehicle.capacities.length) {
          append('p', this.host.t('guide_capacity_heading'), detail);
          const list = document.createElement('ul'); detail.append(list);
          vehicle.capacities.forEach(row => append('li', [row.label + ':', row.value, row.unit, row.condition].filter(Boolean).join(' '), list));
        }
        if (vehicle.disclaimer) append('p', vehicle.disclaimer, detail);
      }
    }
    clear() { this.invalidate(); this.failedStage = ''; this.clearResult(); this.status(this.ready ? 'guide_live_ready' : 'guide_live_error'); this.setButton(this.ready && !this.host.fieldValues().engine, !this.ready); }
    destroy() { this.destroyed = true; this.garageEpoch += 1; this.garageUnsubscribe?.(); this.garageUnsubscribe = null; this.invalidate(); this.makes = []; this.models = []; this.types = []; }
  }
  const api = { sourceHold, create: host => new LiveGuide(host), LiveGuide, yearIn, usMarket, makeChoices, modelChoices, typeChoices, normalizeRecommendation, plain, garageName, garageMatches };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof window !== 'undefined') window.SacLmGuide = api;
}());
